import { useContext, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, Cross2Icon, FileTextIcon, ImageIcon } from '@radix-ui/react-icons';
import { CheckCircle2, ClipboardList } from 'lucide-react';
import AppContext from '../../contexts/AppContext';
import { Button } from '../../components/ui/button';
import IssueInfo from '../../components/IssueInfo';
import IssueComments from '../../components/IssueComments';
import AttachmentsGallery from '../../components/AttachmentsGallery';
import DocRenderer from '../../components/DocRenderer';
import DetailCard from '../../components/DetailCard';
import RatingCard from '../../components/rating/RatingCard';
import { useIssueDetail } from '../../hooks/useIssueDetail';
import { useIssueAttachments } from '../../hooks/useIssueAttachments';
import { useIssueForms } from '../../hooks/useIssueForms';
import { ISSUE_NOT_FOUND, IssueFormSummary } from '../../api/issueDetailApi';
import {
  resolveDetailFields,
  hasVisibleField,
  ResolvedDetailField,
} from '../../utils/issueDetailFields';
import { getMappedFieldValue } from '../../utils/getMappedFieldValue';
import { useAdminIssueTransitions } from '../../hooks/useAdminIssueTransitions';
import { InterventionsList } from './components/InterventionsList';
import {
  ValidateTransitionDialog,
  ValidateTransitionIssue,
} from './components/ValidateTransitionDialog';

// Same allowlist as the user-facing view (src/components/IssueDetails.js).
const ALLOWED_DETAIL_KEYS = [
  'description',
  'problems',
  'assets',
  'duedate',
  'intervention_time',
  'delivery_date',
  'reception_date',
  'contact_name',
  'contact_email',
  'contact_phone',
  'loading_dock',
  'delivery_time_slot',
  'desired_intervention_date',
  'desired_intervention_time',
];

const ASSETS_KEYS = ['sector', 'shopSign', 'intervention_point', 'brand', 'furniture'];

const getPriorityColor = (priority?: string): string => {
  const colors: Record<string, string> = {
    high: 'bg-red-100 text-red-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-green-100 text-green-800',
  };
  return colors[priority?.toLowerCase() ?? ''] || 'bg-gray-100 text-gray-800';
};

interface SelectedImage {
  url: string;
  filename?: string;
}

const AdminIssueDetail = () => {
  const { t } = useTranslation();
  const { issueKey } = useParams<{ issueKey: string }>();
  const navigate = useNavigate();
  const { currentUser } = useContext(AppContext) as {
    currentUser: { role?: string; name?: string } | null;
  };
  const [selectedImage, setSelectedImage] = useState<SelectedImage | null>(null);

  const { issue, isLoading, error } = useIssueDetail(issueKey);
  const {
    attachments,
    isLoading: attachmentsLoading,
    error: attachmentsError,
  } = useIssueAttachments(issueKey);
  const { forms, isLoading: formsLoading, error: formsError } = useIssueForms(issue?.id);

  // Gate actions: the server decides (validation gate + group membership +
  // transition allowlist). Empty list or 403 → no button rendered.
  const { data: gateTransitions } = useAdminIssueTransitions(issueKey ?? null, !!issue);
  const [isValidateOpen, setIsValidateOpen] = useState(false);
  const canValidate = (gateTransitions?.length ?? 0) > 0;

  const serviceDeskConfig = issue?.serviceDesk ?? null;

  // Minimal shape the validate dialog renders (key, summary, status badge).
  const dialogIssue = useMemo<ValidateTransitionIssue | null>(() => {
    if (!issue) return null;
    const categoryKey = issue.fields.status.statusCategory?.key;
    return {
      issueKey: issue.key,
      summary: issue.mappedFields.summary ?? '',
      status: issue.fields.status.name,
      statusCategory: categoryKey === 'new' || categoryKey === 'done' ? categoryKey : 'indeterminate',
    };
  }, [issue]);

  const currentIssue = useMemo(() => {
    if (!issue) return null;
    const { key, fields, mappedFields, names, participants } = issue;
    return {
      key,
      status: fields.status.name,
      summary: mappedFields.summary,
      fields,
      names,
      mappedFields,
      // Rebuilt objects used to drop them, so the card rendered `null` on both screens (D52).
      participants,
    };
  }, [issue]);

  const goBack = () =>
    navigate(serviceDeskConfig ? `/admin/issues?sd=${serviceDeskConfig.id}` : '/admin/issues');

  const openForm = (form: IssueFormSummary) =>
    navigate(`/admin/issues/${encodeURIComponent(issueKey ?? '')}/forms/${encodeURIComponent(form.id)}`, {
      // IssueForm reads `issueId` from the navigation state; alias it so the
      // already-fetched issue is reused instead of refetched by key.
      state: { issue: { ...issue, issueId: issue?.id } },
    });

  // Static labels for the qualification block: literal translation calls only,
  // so the i18n static guard stays green (same rule as the user view).
  const getAssetLabel = (key: string): string => {
    switch (key) {
      case 'sector':
        return t('Sector');
      case 'shopSign':
        return t('Banner');
      case 'intervention_point':
        return t('Store');
      case 'brand':
        return t('Brand');
      case 'furniture':
        return t('Furniture');
      default:
        return key;
    }
  };

  const resolveDisplayValue = (field: ResolvedDetailField): string | null => {
    if (!field.isEmpty || field.displayValue == null) return field.displayValue;
    switch (field.displayValue) {
      case 'Not specified':
        return t('Not specified');
      default:
        return field.displayValue;
    }
  };

  const renderFieldRows = (fields: ResolvedDetailField[]) =>
    fields
      .filter((field) => field.isADFDoc || field.displayValue != null)
      .map((field, index) => (
        <tr key={`${field.key}-${index}`} className="align-top">
          <th className="w-1/3 px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-left bg-gray-50">
            {field.label}
          </th>
          <td className="px-4 py-3 text-sm text-gray-900">
            {field.isADFDoc ? (
              <DocRenderer doc={field.rawValue} className="text-sm text-gray-900" />
            ) : (
              resolveDisplayValue(field) || '-'
            )}
          </td>
        </tr>
      ));

  const renderFieldsTable = (fields: ResolvedDetailField[]) => (
    <div className="overflow-hidden rounded-lg border border-gray-200">
      <table className="min-w-full text-sm">
        <tbody className="divide-y divide-gray-200">{renderFieldRows(fields)}</tbody>
      </table>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen" role="status">
        <div className="animate-spin rounded-full h-14 w-14 border-b-2 border-black"></div>
        <span className="sr-only">{t('Loading…')}</span>
      </div>
    );
  }

  if (error || !currentIssue) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {error?.message === ISSUE_NOT_FOUND
            ? t('This ticket cannot be found or you do not have access to it.')
            : t('Unable to load the ticket right now. Please try again later.')}
        </h3>
        <button
          onClick={goBack}
          className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors font-medium"
        >
          {t('Back to tickets')}
        </button>
      </div>
    );
  }

  const fieldsMapping = serviceDeskConfig?.fieldsMapping ?? {};
  const allowedKeySet = new Set(ALLOWED_DETAIL_KEYS);

  const detailsSections = (serviceDeskConfig?.detailsSections ?? []).map((section) => ({
    title: section.title,
    keys: (section.keys ?? []).filter((key) => allowedKeySet.has(key)),
  }));
  const sectionedKeys = new Set(detailsSections.flatMap((section) => section.keys));

  const catchAllFields = resolveDetailFields(
    ALLOWED_DETAIL_KEYS.filter((key) => !sectionedKeys.has(key)),
    currentIssue,
    fieldsMapping
  );

  const resolvedSections = detailsSections
    .map((section, index) => ({
      id: `section-${index}`,
      title: section.title,
      fields: resolveDetailFields(section.keys, currentIssue, fieldsMapping),
    }))
    .filter((section) => hasVisibleField(section.fields));

  const visibleAssetKeys = ASSETS_KEYS.filter((key) => currentIssue.mappedFields[key]);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <button
          onClick={goBack}
          className="inline-flex items-center gap-2 font-medium text-black transition-colors hover:text-primary/80"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          {t('Back to tickets')}
        </button>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900 font-mono">{currentIssue.key}</h1>
            <span className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
              {currentIssue.status}
            </span>
          </div>
          <p className="mt-1 text-gray-600">{currentIssue.summary}</p>
        </div>
        {canValidate && (
          <Button onClick={() => setIsValidateOpen(true)} className="shrink-0">
            <CheckCircle2 className="w-4 h-4 mr-2" />
            {t('Validate')}
          </Button>
        )}
      </div>

      <IssueInfo
        issue={currentIssue}
        getPriorityPill={getPriorityColor}
        serviceDeskConfig={serviceDeskConfig}
      />

      {resolvedSections.map((section) => (
        <DetailCard
          key={section.id}
          title={section.title}
          icon={<FileTextIcon className="w-5 h-5" />}
        >
          {renderFieldsTable(section.fields)}
        </DetailCard>
      ))}

      <DetailCard title={t('Interventions')} icon={<ClipboardList className="w-5 h-5" />}>
        {formsError ? (
          <p className="text-sm text-red-600">{formsError.message}</p>
        ) : (
          <InterventionsList forms={forms} isLoading={formsLoading} onOpen={openForm} />
        )}
      </DetailCard>

      <RatingCard issueKey={issueKey ?? ''} readOnly />

      {visibleAssetKeys.length > 0 && (
        <DetailCard title={t('Qualification details')} icon={<FileTextIcon className="w-5 h-5" />}>
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <tbody className="divide-y divide-gray-200">
                {visibleAssetKeys.map((key) => {
                  const value = currentIssue.mappedFields[key];
                  return (
                    <tr key={key} className="align-top">
                      <th className="w-1/3 px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-left bg-gray-50">
                        {getAssetLabel(key)}
                      </th>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {Array.isArray(value) && value.length > 0 ? (
                          <ul className="space-y-1">
                            {value.map((asset, assetIndex) => (
                              <li key={assetIndex} className="font-medium">
                                {asset?.value || t('Item {{number}}', { number: assetIndex + 1 })}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          getMappedFieldValue(value) || '-'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </DetailCard>
      )}

      {hasVisibleField(catchAllFields) && (
        <DetailCard title={t('Request details')} icon={<FileTextIcon className="w-5 h-5" />}>
          {renderFieldsTable(catchAllFields)}
        </DetailCard>
      )}

      <DetailCard
        title={t('Attachments')}
        icon={<ImageIcon className="w-5 h-5" />}
      >
        {attachmentsError ? (
          <p className="text-sm text-red-600">{attachmentsError.message}</p>
        ) : (
          <AttachmentsGallery
            attachments={attachments}
            loading={attachmentsLoading}
            onImageClick={setSelectedImage}
            onDelete={undefined}
          />
        )}
      </DetailCard>

      <IssueComments issueKey={issueKey} currentUser={currentUser} onImageClick={setSelectedImage} />

      <ValidateTransitionDialog
        issue={isValidateOpen ? dialogIssue : null}
        onClose={() => setIsValidateOpen(false)}
      />

      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-full max-h-full">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors flex items-center gap-2 bg-black/50 px-4 py-2 rounded-lg text-sm"
            >
              <Cross2Icon className="w-5 h-5" />
              {t('Close')}
            </button>
            <img
              src={selectedImage.url}
              alt={selectedImage.filename ?? ''}
              className="w-full max-h-96 object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminIssueDetail;
